import { parseISO } from 'date-fns';
import type { LocationQueryRaw, LocationQueryValue } from 'vue-router';
import { getIsoDate, type SeatType } from '@cvo/domain';
import type { SearchTripParams } from '@cvo/ui/components/domain';

/** The URL form of the params that make up a search. */
export function toSearchQuery(params: SearchTripParams) {
  return {
    origin: params.origin,
    destination: params.destination,
    departDate: params.departDate ? getIsoDate(params.departDate) : undefined,
    returnDate: params.returnDate ? getIsoDate(params.returnDate) : undefined,
    tripType: params.tripType,
    passengerIds: params.passengerIds,
    seatTypes: toSeatTypesQuery(params.seatTypes),
  };
}

const SEAT_TYPE_SEPARATOR = ':';

/** Seat choices travel as repeatable `personId:SEAT_TYPE` entries, so they round-trip like the id lists. */
function toSeatTypes(value: LocationQueryValue | LocationQueryValue[]): Record<string, SeatType> | undefined {
  const entries = (toPassengerIds(value) ?? []).flatMap((entry) => {
    const [id, seatType] = entry.split(SEAT_TYPE_SEPARATOR);
    return id && seatType ? [[id, seatType as SeatType] as const] : [];
  });

  return entries.length ? Object.fromEntries(entries) : undefined;
}

/** Vue Router collapses a single-item array query back to a plain string on read, so both shapes must be normalized. */
function toPassengerIds(value: LocationQueryValue | LocationQueryValue[]): string[] | undefined {
  const ids = (Array.isArray(value) ? value : [value]).filter((id): id is string => !!id);
  return ids.length ? ids : undefined;
}

/** Inverse of the parsing above. Undefined when there is nothing to store. */
export function toSeatTypesQuery(seatTypes: SearchTripParams['seatTypes']): string[] | undefined {
  const entries = Object.entries(seatTypes ?? {});
  return entries.length ? entries.map(([id, seatType]) => `${id}${SEAT_TYPE_SEPARATOR}${seatType}`) : undefined;
}

/**
 * Low-level adapter between the URL query and the booking search parameters.
 *
 * Owns the translation only — it has no notion of trip selection, city filtering or history
 * semantics (those live in {@link useBookingUrlState}). Higher layers build on top of it.
 *
 * @returns The raw query strings (`searchQueryParams`), their typed/parsed form (`searchParams`:
 * ISO dates → `Date`, passengers → `number`) and `setSearchParams`, the raw full-query replace
 * primitive (`router.replace`).
 */
export function useSearchParamsAdapter() {
  const route = useRoute();
  const router = useRouter();

  /** List of search query parameters values as strings */
  const searchQueryParams = computed(() => {
    return {
      origin: route.query.origin,
      destination: route.query.destination,
      departDate: route.query.departDate,
      returnDate: route.query.returnDate,
      passengerIds: route.query.passengerIds,
      seatTypes: route.query.seatTypes,
      tripType: route.query.tripType,
    };
  });

  /** Mapped version of search query parameters. Dates are optional: an absent date means "anytime". */
  const searchParams = computed<SearchTripParams>(() => {
    return {
      origin: searchQueryParams.value.origin as string | undefined ?? '',
      destination: searchQueryParams.value.destination as string | undefined ?? '',
      departDate: searchQueryParams.value.departDate ? parseISO(searchQueryParams.value.departDate as string) : undefined,
      returnDate: searchQueryParams.value.returnDate ? parseISO(searchQueryParams.value.returnDate as string) : undefined,
      passengerIds: searchQueryParams.value.passengerIds ? toPassengerIds(searchQueryParams.value.passengerIds) : undefined,
      seatTypes: searchQueryParams.value.seatTypes ? toSeatTypes(searchQueryParams.value.seatTypes) : undefined,
      tripType: searchQueryParams.value.tripType as SearchTripParams['tripType'],
    };
  });

  function setSearchParams(params: LocationQueryRaw) {
    return router.replace({ query: params });
  }

  return {
    searchQueryParams,
    searchParams,
    setSearchParams,
  };
}
